import { contains, ruleMatchesAsset } from "@/domain";
import type {
  AlertEvent,
  Asset,
  LocationFix,
  Place,
  Rule,
  ShareLink,
  WorkspaceQueries,
} from "@/domain";
export function dateTime(
  at: number | null | undefined,
  timeZone: string,
  timeOnly = false,
  seconds = false,
) {
  if (at == null) return "Not yet observed";
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    ...(timeOnly ? {} : { month: "short", day: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" as const } : {}),
  }).format(at);
}
export function dayKey(at: number, timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}
export function placeAt(
  data: Pick<WorkspaceQueries, "listPlaces">,
  fix?: LocationFix,
) {
  return fix ? data.listPlaces().find((p) => contains(p, fix)) : undefined;
}
export function placeCenter(p: Place) {
  const g = p.geometry;
  return g.type === "circle"
    ? g.center
    : {
        lat: g.vertices.reduce((s, v) => s + v.lat, 0) / g.vertices.length,
        lng: g.vertices.reduce((s, v) => s + v.lng, 0) / g.vertices.length,
      };
}
export function sourceLabel(fix?: LocationFix) {
  return fix?.source === "gateway"
    ? "Nearby gateway"
    : fix?.source === "phone"
      ? "Nearby phone"
      : "Nearby phone or gateway";
}
export function freshness(data: WorkspaceQueries, asset: Asset) {
  const at = data.getLatestLocation(asset.id)?.seenAt;
  if (!at) return "Awaiting first location";
  const thresholds = data
    .listRules("not_seen")
    .filter(
      (r) =>
        r.enabled &&
        ruleMatchesAsset(r, asset, { shipments: r.scope.kind === "shipment" ? data.listShipments() : [] }),
    )
    .map((r) => r.thresholdMs)
    .filter((t): t is number => t !== null);
  const threshold = thresholds.length ? Math.min(...thresholds) : 6 * 3600000;
  return data.now - at >= threshold
    ? "Needs attention"
    : data.now - at <= 30 * 60000
      ? "Seen recently"
      : "Seen earlier";
}
export const ruleLabel: Record<Rule["type"], string> = {
  perimeter_exit: "Departure",
  perimeter_enter: "Arrival",
  destination_arrival: "Destination arrival",
  checkpoint: "Checkpoint",
  not_seen: "Not seen",
  dwell: "Dwell time",
};
export function alertLabel(data: WorkspaceQueries, alert: AlertEvent) {
  const a = data.getAsset(alert.assetId)?.name ?? "Asset";
  const r = data.getRule(alert.ruleId);
  const p = alert.placeId ? data.getPlace(alert.placeId)?.name : undefined;
  if (r?.type === "not_seen") {
    const minutes = Math.round((r.thresholdMs ?? 21600000) / 60000);
    const amount = minutes < 60 ? minutes : minutes / 60;
    const unit = minutes < 60 ? "minute" : "hour";
    return `${a} not seen for ${amount} ${unit}${amount === 1 ? "" : "s"}`;
  }
  return `${a} ${r?.type === "perimeter_exit" ? "left" : r?.type === "dwell" ? "stayed at" : "arrived at"} ${p ?? "a saved place"}`;
}
export function shareStatus(s: ShareLink, now = Date.now()) {
  return s.revokedAt ? "Revoked" : s.expiresAt <= now ? "Expired" : "Active";
}
export function sentence(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ");
}
export function localInput(at: number) {
  const d = new Date(at);
  return new Date(at - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

// Named road/city anchors describe an observation without claiming a saved boundary.
const LOCATION_ANCHORS: [string, number, number][] = [
  ['US 101 near San Bruno', 37.635, -122.407], ['US 101 near South San Francisco', 37.6639, -122.401],
  ['US 101 near Brisbane', 37.706, -122.405], ['San Francisco, Mission District', 37.761, -122.42],
  ['San Francisco, SoMa', 37.779, -122.407], ['San Francisco, Presidio', 37.8, -122.46],
  ['Seattle, Capitol Hill', 47.614, -122.319], ['I-5 near Tukwila', 47.505, -122.297],
  ['Port of Oakland', 37.795, -122.299], ['I-5 near Bakersfield', 35.38, -119.04],
  ['I-5 near Lebec', 34.85, -118.87], ['Port Hueneme', 34.148, -119.205],
  ['Long Beach', 33.78, -118.224], ['Los Angeles', 34.01, -118.23],
  ['I-10 near Ontario', 34.064, -117.61], ['I-10 near Banning', 33.929, -116.877],
  ['I-10 near Indio', 33.728, -116.22], ['I-10 near Desert Center', 33.714, -115.40],
  ['I-10 near Blythe', 33.61, -114.58], ['I-10 near Quartzsite', 33.66, -114.23],
  ['I-10 near Buckeye', 33.45, -112.59], ['Phoenix, west industrial district', 33.44, -112.15],
  ['I-10 near Phoenix', 33.461, -112.057], ['Tempe', 33.415, -111.94], ['Mesa', 33.415, -111.832], ['Glendale', 33.539, -112.18],
  ['I-10 near Casa Grande', 32.879, -111.757], ['I-10 near Tucson', 32.139, -110.946],
  ['I-10 near Willcox', 32.267, -109.832], ['I-10 near Deming', 32.255, -107.755],
  ['I-10 near Las Cruces', 32.286, -106.766], ['El Paso', 31.7735, -106.3712],
  ['I-10 near Van Horn', 31.045, -104.839], ['I-10 near Fort Stockton', 30.885, -102.884],
  ['I-20 near Midland', 31.998, -102.079], ['I-20 near Abilene', 32.448, -99.733],
  ['I-20 near Fort Worth', 32.746, -97.329], ['Dallas', 32.767, -96.904],
  ['US 93 near Wickenburg', 33.9, -112.66], ['US 93 near Congress', 34.23, -112.97],
  ['I-40 near Kingman', 35.19, -114.05], ['I-40 near Flagstaff', 35.19, -111.65],
  ['I-40 near Gallup', 35.53, -108.74], ['Albuquerque', 35.1265, -106.6003],
  ['I-15 near Barstow', 34.897, -117.024], ['I-15 near Primm', 35.61, -115.39],
  ['I-11 near Boulder City', 35.98, -114.83], ['Las Vegas', 36.2365, -115.098],
  ['San Diego, Mission Valley', 32.767, -117.154], ['Riverside', 33.94, -117.28],
];
export function locationLabel(data: Pick<WorkspaceQueries, 'listPlaces'>, fix?: LocationFix) {
  if (!fix) return 'Not yet seen';
  const place = placeAt(data, fix);
  if (place) return place.name;
  let nearest = LOCATION_ANCHORS[0], distance = Infinity;
  for (const anchor of LOCATION_ANCHORS) {
    const d = (anchor[1] - fix.lat) ** 2 + ((anchor[2] - fix.lng) * Math.cos(fix.lat * Math.PI / 180)) ** 2;
    if (d < distance) { nearest = anchor; distance = d; }
  }
  return distance < 0.12 ? nearest[0] : `Near ${nearest[0].replace(/^.* near /, '')}`;
}
export const shipmentStatus = (status: string) => status === 'attention' ? 'Delayed' : sentence(status);
